import {
  FiBookOpen,
  FiClock,
  FiHome,
  FiLayers,
  FiShield,
  FiTarget,
  FiTrendingUp,
} from 'react-icons/fi';

export const PLANNING_OUTCOMES = [
  {
    id: 'retirement',
    icon: FiClock,
    path: '/planning/retirement',
    en: {
      title: 'Save for retirement',
      description: 'Build a plan for the retirement you want.',
      action: 'Plan retirement',
    },
    fr: {
      title: 'Épargner pour la retraite',
      description: 'Estimez votre préparation, les écarts de revenu et les comptes importants.',
      action: 'Planifier la retraite',
    },
  },
  {
    id: 'home',
    icon: FiHome,
    path: '/planning/accounts',
    en: {
      title: 'Buy a home',
      description: 'Understand your options and plan with confidence.',
      action: 'Explore home savings',
    },
    fr: {
      title: 'Acheter une maison',
      description: 'Comprenez le CELIAPP, le CELI, les délais et la mise de fonds.',
      action: 'Explorer l’épargne logement',
    },
  },
  {
    id: 'accounts',
    icon: FiBookOpen,
    path: '/planning/accounts',
    en: {
      title: 'Understand TFSA/RRSP/FHSA',
      description: 'Learn how Canadian accounts work and which one fits.',
      action: 'Learn accounts',
    },
    fr: {
      title: 'Comprendre les comptes canadiens',
      description: 'Apprenez le CELI, REER, CELIAPP, REEE et FERR en langage simple.',
      action: 'Apprendre les comptes',
    },
  },
  {
    id: 'forecast',
    icon: FiTrendingUp,
    path: '/planning/forecast',
    en: {
      title: 'Forecast future wealth',
      description: 'See how today’s choices can shape your tomorrow.',
      action: 'Run a forecast',
    },
    fr: {
      title: 'Projeter le patrimoine futur',
      description: 'Comparez des résultats possibles sans expertise de marché.',
      action: 'Lancer une projection',
    },
  },
];

export const PLANNING_MODULES = [
  {
    id: 'start',
    icon: FiTarget,
    path: '/planning',
    group: 'foundation',
    en: {
      title: 'Start here',
      description: 'Choose the financial question you want to answer first.',
      action: 'Start',
    },
    fr: {
      title: 'Commencer ici',
      description: 'Choisissez la question financière à clarifier en premier.',
      action: 'Commencer',
    },
  },
  {
    id: 'accounts',
    icon: FiBookOpen,
    path: '/planning/accounts',
    group: 'foundation',
    en: {
      title: 'Learn accounts',
      description: 'Match goals to TFSA, RRSP, FHSA, RESP, and RRIF basics.',
      action: 'Learn',
    },
    fr: {
      title: 'Apprendre les comptes',
      description: 'Reliez vos objectifs au CELI, REER, CELIAPP, REEE et FERR.',
      action: 'Apprendre',
    },
  },
  {
    id: 'concepts',
    icon: FiLayers,
    path: '/planning/concepts',
    group: 'foundation',
    en: {
      title: 'Learn investing',
      description: 'Understand compounding, inflation, risk, and diversification.',
      action: 'Open concepts',
    },
    fr: {
      title: 'Apprendre à investir',
      description: 'Comprenez la capitalisation, l’inflation, le risque et la diversification.',
      action: 'Ouvrir les concepts',
    },
  },
  {
    id: 'goals',
    icon: FiTarget,
    path: '/planning/goals',
    group: 'action',
    en: {
      title: 'Set a goal',
      description: 'Turn a future purchase or milestone into a monthly savings target.',
      action: 'Set goal',
    },
    fr: {
      title: 'Définir un objectif',
      description: 'Transformez un projet en cible d’épargne mensuelle.',
      action: 'Définir',
    },
  },
  {
    id: 'forecast',
    icon: FiTrendingUp,
    path: '/planning/forecast',
    group: 'action',
    en: {
      title: 'Forecast growth',
      description: 'Model conservative, moderate, and growth scenarios.',
      action: 'Forecast',
    },
    fr: {
      title: 'Projeter la croissance',
      description: 'Modélisez des scénarios prudents, modérés et dynamiques.',
      action: 'Projeter',
    },
  },
  {
    id: 'retirement',
    icon: FiShield,
    path: '/planning/retirement',
    group: 'action',
    en: {
      title: 'Retirement planning',
      description: 'Estimate income, benefits, and readiness before choosing next steps.',
      action: 'Plan retirement',
    },
    fr: {
      title: 'Planification de retraite',
      description: 'Estimez le revenu, les prestations et la préparation.',
      action: 'Planifier',
    },
  },
];

export const ACCOUNT_LEARNING_PATHS = [
  {
    id: 'retirement',
    accountIds: ['rrsp', 'tfsa', 'rrif'],
    en: {
      title: 'Saving for retirement',
      description: 'Start with RRSP, TFSA, and RRIF rules.',
    },
    fr: {
      title: 'Épargner pour la retraite',
      description: 'Commencez par le REER, le CELI et le FERR.',
    },
  },
  {
    id: 'home',
    accountIds: ['fhsa', 'tfsa', 'rrsp'],
    en: {
      title: 'Buying a home',
      description: 'Compare FHSA, TFSA, and first-home withdrawal rules.',
    },
    fr: {
      title: 'Acheter une maison',
      description: 'Comparez le CELIAPP, le CELI et les règles de premier achat.',
    },
  },
  {
    id: 'children',
    accountIds: ['resp', 'tfsa'],
    en: {
      title: 'Saving for children',
      description: 'Learn RESP grants and when a TFSA may help.',
    },
    fr: {
      title: 'Épargner pour les enfants',
      description: 'Comprenez les subventions REEE et le rôle possible du CELI.',
    },
  },
  {
    id: 'tax',
    accountIds: ['tfsa', 'rrsp', 'fhsa'],
    en: {
      title: 'Tax-efficient investing',
      description: 'See how account choice can affect after-tax outcomes.',
    },
    fr: {
      title: 'Investir efficacement après impôt',
      description: 'Voyez comment le choix du compte influence le résultat net.',
    },
  },
];

export const localizeContent = (item, lang) => item?.[lang] || item?.en || {};
